import { useEffect, useMemo } from 'react';
import { useCoreLoopConfigurationStore } from '@core/coreLoopConfiguration/useCoreLoopConfigurationStore';
import usePortalAccess from '@/access/usePortalAccess';
import { useTenantId } from '@/contexts/TenantContext';
import type { FeatureViewInput } from '@/view';

const EMPTY_CLIENT_VISIBLE_ASSETS: string[] = [];

const useFeatureView = <TView>(
  getView: (input: FeatureViewInput) => TView,
): TView => {
  const access = usePortalAccess();
  const tenantId = useTenantId();
  const { coreLoopConfigurationItems, ensureCoreLoopConfiguration } =
    useCoreLoopConfigurationStore();

  useEffect(() => {
    if (tenantId) {
      void ensureCoreLoopConfiguration(tenantId);
    }
  }, [tenantId, ensureCoreLoopConfiguration]);

  const clientsCanSeePayRates =
    !!coreLoopConfigurationItems?.clientsCanSeePayRates;
  const tenantRequiresPostingQuestionnaire = Boolean(
    coreLoopConfigurationItems?.tenantRequiresPostingQuestionnaire,
  );
  const clientVisibleAssets = Array.isArray(
    coreLoopConfigurationItems?.clientVisibleAssets,
  )
    ? coreLoopConfigurationItems.clientVisibleAssets
    : EMPTY_CLIENT_VISIBLE_ASSETS;
  const clientsCanEditJobs = Boolean(
    coreLoopConfigurationItems?.clientsCanEditJobs,
  );
  const clientsCanSeeAssignedCandidateEmail = Boolean(
    coreLoopConfigurationItems?.clientsCanSeeAssignedCandidateEmail,
  );
  const clientsCanSeeAssignedCandidatePhone = Boolean(
    coreLoopConfigurationItems?.clientsCanSeeAssignedCandidatePhone,
  );

  return useMemo(
    () =>
      getView({
        access,
        clientsCanSeePayRates,
        tenantRequiresPostingQuestionnaire,
        clientVisibleAssets,
        clientsCanEditJobs,
        clientsCanSeeAssignedCandidateEmail,
        clientsCanSeeAssignedCandidatePhone,
      }),
    [
      getView,
      access,
      clientsCanSeePayRates,
      tenantRequiresPostingQuestionnaire,
      clientVisibleAssets,
      clientsCanEditJobs,
      clientsCanSeeAssignedCandidateEmail,
      clientsCanSeeAssignedCandidatePhone,
    ],
  );
};

export default useFeatureView;
